import {
	commonErrorResponseInterceptor,
	type Error,
	EventStore,
	getMissingCredentials,
	getMissingCredentialsErrorMessage,
	Logger,
	noop,
	NotificationService,
	type ParsedApiErrorResponse,
	pubSubService,
	ResponseStatus429Error,
	ResponseStatus503Error,
	ResponseStatus50xError,
	type SociosApiInterceptors,
	type Unpack
} from '@capsicum/common';
import * as Sentry from '@sentry/nextjs';
import { AxiosError } from 'axios';
import { match } from 'ts-pattern';

import { useRouter } from '../../../../hooks/router/useCustomRouter';
import { Snackbar } from '../../../../ui/molecules/snackbar/Snackbar';
import { Maintenance } from '../../../../ui/pages/errors/Maintenance';

type Props = Unpack<Parameters<typeof commonErrorResponseInterceptor>>;

export const useErrorResponseInterceptor: (props?: Props) => SociosApiInterceptors['responseError'] = _props => {
	const { replace } = useRouter();

	return commonErrorResponseInterceptor({
		onApiError: parsedErrorNotification,
		onAxiosError: error => {
			match(error)
				.with({ code: AxiosError.ERR_NETWORK }, () =>
					pubSubService.publish('globalError', new ResponseStatus503Error(Maintenance, error))
				)
				.when(({ code }) => [AxiosError.ECONNABORTED, AxiosError.ETIMEDOUT].includes(code ?? ''), noop)
				.when(
					({ name }) => name === 'AbortError',
					() => noop
				)
				.otherwise(axiosErrorNotification);
		},
		onStatusCode: {
			401: () => replace('/login'),
			403: (error, parsedError) => {
				const missingCredential = getMissingCredentials(error);

				if (missingCredential) {
					const message = getMissingCredentialsErrorMessage(error?.response?.data as Error);

					Sentry.captureMessage(message);

					return;
				}

				// TODO - Remove if condition and move to 401 once BE update ApiProxy and return the correct status code
				if (error?.response?.data?.message?.includes('You cannot consume this service')) {
					Sentry.captureMessage('[401] - Invalid Session');
					pubSubService.publish(EventStore.SESSION_EXPIRED);

					return;
				}
				parsedErrorNotification(error, parsedError);
			},
			404: parsedErrorNotification,
			429: error => pubSubService.publish('globalError', new ResponseStatus429Error(error)),
			451: () => replace('/unsupported-country'),
			500: error => pubSubService.publish('globalError', new ResponseStatus50xError(error)),
			501: error => pubSubService.publish('globalError', new ResponseStatus50xError(error)),
			502: error => pubSubService.publish('globalError', new ResponseStatus50xError(error)),
			503: error => pubSubService.publish('globalError', new ResponseStatus503Error(Maintenance, error)),
			504: parsedErrorNotification
		}
	});
};

const axiosErrorNotification = (error: AxiosError) => {
	Logger.error(error.message || error.code || error.name, error);
	NotificationService.debouncedAddNotification({
		component: <Snackbar $description="error.50x.description" $title="error.50x.title" variant="error" />
	});
};

const parsedErrorNotification = (error: any, { requestId }: ParsedApiErrorResponse) => {
	Logger.error(error.message || error.code || error.name || requestId, error);
	NotificationService.debouncedAddNotification({
		component: (
			<Snackbar
				$description="error.504.description"
				$title="error.504.title"
				notificationId={requestId}
				variant="error"
			/>
		)
	});
};
